/**
 * 再実行が止まったときの「復旧」（Issue #618）。以前は赤い帯に「再実行が止まりました（mismatch @ 30）」と出るだけで手段が無かった。
 *   - 何手目・動画の何秒の応答で止まったかと、理由（日本語）。その手へ動画を飛ばす
 *   - 自動で付け直す: 記録済みの応答を全部、選んだカードで付け直す（サーバーの OcgRematchOps）。ページが開いたときに確かめだけ走らせ
 *     （check）、最後まで通るなら 1 ボタン、途中までなら「止まる手から後ろを消して」付け直す
 *   - ここから入力し直す: 止まった手から後ろを消す（末尾から count 手の取り消し）
 * どのボタンもサーバーが書く前に再実行して確かめる。
 */
import { fmtTime } from './PlaybackTab';
import { btn } from './RespondTab';

export const STOP_REASONS = {
  mismatch: '記録した応答が、今の問い合わせと合いません（デッキや登録を変えて、候補の並びがずれたときに起きます）',
  rejected: '記録した応答を再現エンジンが受け付けません',
  rematch_failed: '選んだカードが候補にありません',
  injection_missing: '登録したカードが、その時点のデッキにありません',
  injection_exhausted: '登録したカードが足りません',
  unparsed: 'まだ対応していない問い合わせです',
  unsupported: 'まだ対応していない問い合わせです',
  max_steps: '再実行が長すぎて打ち切りました',
};
export const stopReasonText = (reason) => STOP_REASONS[reason] ?? `再実行が止まりました（${reason ?? '不明'}）`;

/** 付け直しの確かめの結果から、出す案内とボタン（純関数。テストで見る） */
export function recoveryPlan(stopped, check, total) {
  const at = Number(stopped?.at_seq ?? 0);
  const truncate = { count: Math.max(0, total - at), from: at + 1 };
  if (!check) return { kind: 'checking', truncate };
  if (check.error) return { kind: 'error', text: check.error, truncate };
  if (check.replayable >= check.total) return { kind: 'full', rematched: check.rematched ?? 0, truncate };
  if (check.replayable > at) return { kind: 'partial', upto: check.replayable, drop: check.total - check.replayable, truncate };
  return { kind: 'none', truncate };
}

export default function RecoveryPanel({ stopped, total = 0, opMs = null, check = null, busy = false, onSeek, onRematch, onTruncate }) {
  if (!stopped) return null;
  const at = Number(stopped.at_seq ?? 0);
  const plan = recoveryPlan(stopped, check, total);
  const confirmTruncate = () => {
    const ok = typeof window === 'undefined' || !window.confirm
      || window.confirm(`${at + 1} 手目から後ろの ${plan.truncate.count} 手を消して、ここから入力し直します。よろしいですか？`);
    if (ok) onTruncate?.(plan.truncate.count);
  };
  return (
    <div role="alert" data-testid="recovery-panel" style={{ background: '#3a1a22', color: '#fde', padding: '8px 10px', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontWeight: 800, fontSize: 13 }}>
        再生が {at + 1} 手目で止まりました（全 {total} 手）{opMs != null ? `・動画の ${fmtTime(opMs / 1000)}` : ''}
      </div>
      <div>{stopReasonText(stopped.reason)}</div>
      {stopped.detail ? <div style={{ opacity: 0.6, fontSize: 11, wordBreak: 'break-all' }}>{stopped.detail}</div> : null}
      <div data-testid="recovery-check" style={{ opacity: 0.9 }}>
        {plan.kind === 'checking' ? '選んだカードで付け直せるか確かめています…'
          : plan.kind === 'full' ? `選んだカードで付け直すと、最後まで通ります（付け直すのは ${plan.rematched} 手）`
            : plan.kind === 'partial' ? `付け直すと ${plan.upto} 手目まで通ります（後ろの ${plan.drop} 手は消えます）`
              : plan.kind === 'error' ? `付け直しを確かめられませんでした: ${plan.text}`
                : '付け直しても通りません。止まった手から入力し直してください'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {opMs != null && onSeek ? <button type="button" data-testid="recovery-seek" style={btn('plain')} onClick={() => onSeek(opMs)}>▶ その手へ</button> : null}
        {plan.kind === 'full' ? <button type="button" data-testid="recovery-rematch" disabled={busy} style={btn('primary')} onClick={() => onRematch?.(false)}>自動で付け直す</button> : null}
        {plan.kind === 'partial' ? (
          <button type="button" data-testid="recovery-rematch-drop" disabled={busy} style={btn('primary')} onClick={() => onRematch?.(true)}>
            付け直して {plan.upto + 1} 手目から後ろを消す
          </button>
        ) : null}
        {plan.truncate.count > 0 ? (
          <button type="button" data-testid="recovery-truncate" disabled={busy} style={btn('danger')} onClick={confirmTruncate}>
            {plan.truncate.from} 手目から入力し直す（{plan.truncate.count} 手を消す）
          </button>
        ) : null}
      </div>
    </div>
  );
}
